<script setup lang="ts">
import { ref, onMounted, computed, nextTick } from 'vue'
import { callOnce, useHead } from 'nuxt/app'
import { useImage } from '#imports'
import { useApiFetch } from '../composables/useApiFetch'
import type PopupModal from "../components/PopupModal.vue";
import ExternalLinkIcon from '~/assets/svg/external-link.svg?component'

const popupModalRef = ref<InstanceType<typeof PopupModal> | null>(null)

const { data: courses } = await useApiFetch<any[]>(`/course/active`)
const { data: popupData } = await useApiFetch<any[]>(`/popup/active`)
const { data: categoriesData } = await useApiFetch<any[]>(`/category`)



const selectedCategory = ref('Tümü')
const categories = computed(() => {
  // Sadece mevcut kurslarda bulunan kategorileri göster
  const allCategories = (courses.value || [])
    .flatMap((c: any) => {
      if (Array.isArray(c.category)) {
        return c.category
      }
      if (typeof c.category === 'string' && c.category.trim() !== '') {
        return [c.category.trim()]
      }
      return []
    })
    .filter(Boolean)
  const uniqueCategories = [...new Set(allCategories)]
  return ['Tümü', ...uniqueCategories]
})

const filteredCourses = computed(() => {
  if (!courses.value) return []
  if (selectedCategory.value === 'Tümü') return courses.value
  
  const filtered = courses.value.filter((course: any) => {
    if (Array.isArray(course.category)) {
      const hasCategory = course.category.includes(selectedCategory.value)
      return hasCategory
    }
    if (typeof course.category === 'string') {
      const hasCategory = course.category.trim() === selectedCategory.value
      return hasCategory
    }
    return false
  })

  return filtered
})

const getCategoryColor = (category: string) => {
  const colors = [
    { bg: 'bg-blue-100', text: 'text-blue-800' },
    { bg: 'bg-green-100', text: 'text-green-800' },
    { bg: 'bg-purple-100', text: 'text-purple-800' },
    { bg: 'bg-yellow-100', text: 'text-yellow-800' },
    { bg: 'bg-red-100', text: 'text-red-800' },
    { bg: 'bg-indigo-100', text: 'text-indigo-800' },
    { bg: 'bg-pink-100', text: 'text-pink-800' },
    { bg: 'bg-gray-100', text: 'text-gray-800' }
  ]

  const index = category.length % colors.length
  return colors[index]
}


useHead(() => {
  const firstSixCourses = courses.value?.slice(0, 6) || []
  return {
    meta: [
      {
        name: 'description',
        content:
            "Hayrât Eğitim: Kur’ân-ı Kerîm ve Osmanlı Türkçesi alanlarında çevrim içi ve yüz yüze eğitimler, kurslar ve yarışmalar."
      }
    ],
    link: firstSixCourses.map((course: any) => ({
      rel: 'preload',
      as: 'image',
      href: course.image_url,
      fetchpriority: 'high',
      loading: 'eager'
    }))
  }
})

const normalizeText = (text: string) => {
  if (!text) return ''
  return text
      .replace(/\r\n|\r|\n/g, ' ')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
}

onMounted(async () => {
  await nextTick()
  
  callOnce(() => {
    if (popupData.value && popupData.value.length > 0) {
      if (popupModalRef.value) {
        popupModalRef.value.showWithData(popupData.value)
      }
    }
  })
})
</script>

<template>
  <div class="relative min-h-screen">
    <div class="absolute inset-0 bg-[url('/gomulu-logo.webp')] bg-no-repeat bg-center bg-contain opacity-5 pointer-events-none z-0"></div>
      <div class="pt-8 pb-28">
        <div class="mx-auto max-w-7xl px-2 md:px-6 lg:px-8">
          <div class="mx-auto max-w-[1216px] text-center rounded-2xl md:pt-8 lg:pt-8">
            <h1 class="mx-auto text-[30px] font-[700] leading-[40px] w-full max-w-[198px] h-auto min-h-[40px]">Hayrât
              Eğitim</h1>
            <p class="mx-auto mt-2 w-full lg:max-w-[964px] max-w-[964px] md:max-w-[656px] h-auto min-h-[28px] text-[18px] leading-[28px] font-normal text-gray-500 font-inter">
              Kur'ân-ı Kerîm ve Osmanlı Türkçesi alanında kapsamlı eğitim alın, çeşitli sunumlarla bilgi dağarcığınızı genişletin ve farklı alanlardaki yarışmalarımıza katılın.
            </p>

            <div class="w-full mt-8">
              <div class="flex flex-wrap justify-center" style="gap: 8px;">
                <button
                    v-for="category in categories"
                    :key="category"
                    @click="selectedCategory = category"
                    :class="[
                'h-[38px] text-xs font-medium rounded-[6px] border transition-all duration-300 flex items-center justify-center whitespace-nowrap font-inter leading-5 tracking-0',
                selectedCategory === category
                  ? 'bg-blue-600 text-white border-[#4F46E5] px-[13px] py-[9px]'
                  : 'bg-white text-[#374151] border-[#E5E7EB] hover:bg-[#F9FAFB] hover:border-[#9CA3AF] px-[13px] py-[9px]'
              ]"
                >
                  <svg
                      v-show="selectedCategory === category"
                      class="w-4 h-4 mr-2 flex-shrink-0 transition-all duration-200 ease-in-out"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                  >
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                  {{ category.trim() }}
                </button>
              </div>
            </div>
          </div>


          <div class="mx-auto mt-18 grid max-w-2xl grid-cols-1 gap-x-5 gap-y-[72px] lg:mx-0 lg:max-w-none lg:grid-cols-3">
            <NuxtLink :href="course.link" target="_blank" external v-for="(course,index) in filteredCourses" :key="course.id">
              <article class="flex flex-col items-start justify-between hover:bg-[#F5F6F8] active:bg-[#F5F6F8] focus:bg-[#F5F6F8] rounded-3xl duration-400 cursor-pointer p-2">
              <div class="relative w-full h-48 sm:h-56 lg:h-64 rounded-3xl overflow-hidden bg-gray-50">
                <nuxt-img
                    format="webp"
                    :src="course.image_url"
                    :width="40"
                    :quality="20"
                    class="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-70"
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                />
                <nuxt-img
                    format="webp"
                    :alt="course.title || 'Hayrât Eğitim Online Kursu'"
                    :draggable="false"
                    :src="course.image_url"
                    class="relative w-full h-full object-contain"
                    :preload="index === 0"
                    :fetchpriority="index === 0 ? 'high' : 'low'"
                    :loading="index === 0 ? 'eager' : 'lazy'"
                />
              </div>
              <div class="flex max-w-[700px] grow flex-col justify-between">
                <div class="mt-4 flex items-center gap-x-2 text-xs flex-wrap">
                  <template v-if="Array.isArray(course.category) && course.category.length > 0">
                    <span
                      v-for="category in course.category"
                      :key="category"
                      :class="getCategoryColor(category).bg + ' ' + getCategoryColor(category).text"
                      class="inline-flex items-center justify-center rounded-full px-2.75 py-1.25 text-[12px] font-medium"
                    >
                      {{ category.trim() }}
                    </span>
                  </template>
                  <template v-else>
                    <span :class="getCategoryColor('Genel').bg + ' ' + getCategoryColor('Genel').text" class="inline-flex items-center rounded-full px-3 py-1.5 font-medium">
                      {{ course.category || 'Genel' }}
                    </span>
                  </template>
                </div>
                <div class="group relative grow">
                  <h3 class="mt-4 text-[#4B5563] text-[20px] leading-[28px] font-semibold text-gray-900 group-hover:text-gray-600 group-active:text-gray-600 group-focus:text-gray-600 font-inter tracking-[0%]" style="font-weight: 600;">
                      <span class="absolute inset-0" />
                      {{ course.title }}
                  </h3>
                  <p class="mt-1 text-[16px] leading-[28px] text-[#4B5563] text-sm/6 text-gray-500 font-inter font-sans tracking-[0%]">{{ normalizeText(course.summary) }}</p>
                </div>
              </div>
              </article>
            </NuxtLink>
          </div>
        </div>
      </div>
    <PopupModal ref="popupModalRef" />
  </div>
</template>
